'use client';

import {
  Root,
  Arrow,
  Portal,
  Content,
  Trigger,
  Provider,
} from '@radix-ui/react-tooltip';

import type { ComponentProps, JSX } from 'react';

import { cn } from '~/utils';

export function TooltipProvider({
  delayDuration = 0,
  ...props
}: ComponentProps<typeof Provider>): JSX.Element {
  return (
    <Provider
      data-slot='tooltip-provider'
      delayDuration={delayDuration}
      {...props}
    />
  );
}

export function Tooltip(props: ComponentProps<typeof Root>): JSX.Element {
  return (
    <TooltipProvider>
      <Root data-slot='tooltip' {...props} />
    </TooltipProvider>
  );
}

export function TooltipTrigger(
  props: ComponentProps<typeof Trigger>,
): JSX.Element {
  return <Trigger data-slot='tooltip-trigger' {...props} />;
}

export function TooltipContent({
  children,
  className,
  sideOffset = 0,
  ...props
}: ComponentProps<typeof Content>): JSX.Element {
  return (
    <Portal>
      <Content
        sideOffset={sideOffset}
        data-slot='tooltip-content'
        className={cn(
          'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md text-balance',
          'px-2 py-1 bg-carbon-800 [&>p]:text-white text-xs leading-[130%]',
          className,
        )}
        {...props}
      >
        {children}
        <Arrow className='bg-primary fill-primary z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]' />
      </Content>
    </Portal>
  );
}
